import { PeriodSelector } from "@/components/school-head/PeriodSelector";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useScopedVisits, useSchoolNurses } from "@/hooks/useSchoolHeadData";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Activity, Users, Stethoscope, AlertTriangle, ClipboardList, UserCheck } from "lucide-react";
import { useSchoolHeadPeriod } from "@/contexts/SchoolHeadPeriodContext";

export default function SchoolHeadDashboard() {
  const { user } = useAuth();
  const { selectedYear, selectedTerm } = useSchoolHeadPeriod();
  const { data: visits = [], isLoading: visitsLoading } = useScopedVisits();
  const { data: nurses = [] } = useSchoolNurses();

  const { data: studentCount = 0 } = useQuery({
    queryKey: ["sh-student-count", user?.schoolId],
    enabled: !!user?.schoolId,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("students")
        .select("*", { count: "exact", head: true })
        .eq("school_id", user!.schoolId)
        .eq("is_archived", false);
      if (error) throw error;
      return count || 0;
    },
  });

  const { data: referralCount = 0 } = useQuery({
    queryKey: ["sh-referral-count", user?.schoolId, selectedYear?.id, selectedTerm?.id],
    enabled: !!user?.schoolId,
    queryFn: async () => {
      let q = supabase
        .from("referrals")
        .select("*", { count: "exact", head: true })
        .eq("school_id", user!.schoolId);
      const { count } = await q;
      return count || 0;
    },
  });

  const studentVisits = visits.filter((v) => v.student_id).length;
  const staffVisits = visits.filter((v) => v.community_member_id).length;
  const uniqueStudents = new Set(visits.filter((v) => v.student_id).map((v) => v.student_id)).size;
  const followUps = visits.filter((v) => v.requires_followup).length;

  // Top complaint categories (summarised by simple keyword bucketing)
  const complaintBuckets: Record<string, number> = {};
  for (const v of visits) {
    const c = (v.complaint || "Other").trim().toLowerCase();
    const key = c.split(/[,;.]/)[0].slice(0, 40) || "other";
    complaintBuckets[key] = (complaintBuckets[key] || 0) + 1;
  }
  const topComplaints = Object.entries(complaintBuckets)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  const stats = [
    { icon: Activity, label: "Total visits", value: visits.length, tone: "text-primary" },
    { icon: Stethoscope, label: "Student visits", value: studentVisits, tone: "text-blue-600" },
    { icon: UserCheck, label: "Staff visits", value: staffVisits, tone: "text-purple-600" },
    { icon: Users, label: "Unique students seen", value: uniqueStudents, tone: "text-emerald-600" },
    { icon: AlertTriangle, label: "Awaiting follow-up", value: followUps, tone: "text-amber-600" },
    { icon: ClipboardList, label: "Referrals issued", value: referralCount, tone: "text-rose-600" },
  ];

  return (
    <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">School Health Overview</h1>
          <p className="text-sm text-muted-foreground">
            {user?.schoolName} · {selectedYear?.name}{selectedTerm ? ` · ${selectedTerm.name}` : " · All terms"}
          </p>
        </div>

        <PeriodSelector />

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {stats.map((s) => (
            <Card key={s.label}>
              <CardContent className="flex items-center gap-4 p-5">
                <div className={`rounded-full bg-muted p-3 ${s.tone}`}>
                  <s.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-2xl font-semibold">
                    {visitsLoading ? "…" : s.value.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Most common complaints</CardTitle>
            </CardHeader>
            <CardContent>
              {topComplaints.length === 0 ? (
                <p className="text-sm text-muted-foreground">No visits in this period.</p>
              ) : (
                <ul className="space-y-2">
                  {topComplaints.map(([label, count]) => {
                    const pct = visits.length ? (count / visits.length) * 100 : 0;
                    return (
                      <li key={label} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="capitalize">{label}</span>
                          <span className="text-muted-foreground">{count}</span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded bg-muted">
                          <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">School snapshot</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Active students</span>
                <span className="font-medium">{studentCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Nurses on staff</span>
                <span className="font-medium">{nurses.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Visits per nurse (avg)</span>
                <span className="font-medium">
                  {nurses.length ? Math.round(visits.length / nurses.length) : "—"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Visits per active student</span>
                <span className="font-medium">
                  {uniqueStudents ? (visits.length / uniqueStudents).toFixed(1) : "—"}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
  );
}
